<template>
  <q-dialog
    ref="dialogRef"
    :maximized="$q.screen.lt.md"
    @hide="onDialogHide">
    <q-card
      class="q-dialog-plugin workbench-card column no-wrap"
      :style="$q.screen.lt.md ? '' : 'width: 1200px; max-width: 95vw; height: 85vh'">
      <!-- Header -->
      <q-card-section class="row items-center no-wrap q-py-sm">
        <q-icon
          :name="fileIconFor(previewDocument?.mime_type ?? item?.mime_type)"
          size="26px"
          color="grey-6"
          class="q-mr-sm" />
        <div class="column" style="min-width: 0">
          <div class="text-subtitle1 text-weight-medium ellipsis">
            {{ item?.original_filename || t('queue.unknownFilename') }}
          </div>
          <div class="row items-center q-gutter-xs">
            <StatusPill
              v-if="item"
              :color="STATUS_COLOR[item.status] ?? 'default'"
              :label="t(`status.${item.status}`)" />
            <span
              v-if="item?.queue_step && isActive"
              class="text-caption text-grey-6">
              {{ t(`steps.${item.queue_step}`) }}
            </span>
            <StatusPill
              v-if="item && item.priority !== 'normal'"
              :color="item.priority === 'high' ? 'orange' : 'muted'"
              :label="t(`priority.${item.priority}`)" />
          </div>
        </div>
        <q-space />
        <q-btn
          v-if="item"
          flat
          round
          dense
          size="sm"
          :icon="copied ? symRoundedCheckCircle : symRoundedContentCopy"
          :color="copied ? 'positive' : 'grey-6'"
          @click="copyId">
          <q-tooltip>{{ t('workbench.meta.copyId') }}</q-tooltip>
        </q-btn>
        <q-btn
          flat
          round
          dense
          :icon="symRoundedClose"
          @click="onDialogOK" />
      </q-card-section>

      <q-separator />

      <!-- Body -->
      <q-card-section
        v-if="loading && !item"
        class="col column items-center justify-center">
        <q-spinner size="42px" color="primary" />
      </q-card-section>

      <q-card-section
        v-else-if="!item"
        class="col column items-center justify-center text-grey-6">
        {{ t('workbench.loadFailed') }}
      </q-card-section>

      <q-card-section
        v-else
        class="col column no-wrap q-pa-none workbench-body">
        <!-- Mobile: the preview and the details become tabs -->
        <q-tabs
          v-if="$q.screen.lt.md"
          v-model="mobileTab"
          dense
          no-caps
          class="text-grey-7"
          active-color="primary"
          indicator-color="primary"
          align="justify">
          <q-tab
            name="details"
            :icon="symRoundedInfo"
            :label="t('workbench.tabs.details')" />
          <q-tab
            name="document"
            :icon="symRoundedDescription"
            :label="t('workbench.tabs.document')" />
        </q-tabs>
        <q-separator v-if="$q.screen.lt.md" />

        <div class="col row no-wrap workbench-panes">
          <!-- Document preview pane -->
          <div
            v-show="!$q.screen.lt.md || mobileTab === 'document'"
            class="workbench-preview column"
            :class="{ 'workbench-preview--mobile': $q.screen.lt.md }">
            <div
              v-if="previewDocument"
              class="row items-center justify-end q-px-sm q-py-xs q-gutter-xs">
              <q-btn
                flat
                round
                dense
                size="sm"
                color="grey-7"
                :icon="symRoundedOpenInNew"
                :href="previewDocument.original_url"
                target="_blank">
                <q-tooltip>{{ t('workbench.openFile') }}</q-tooltip>
              </q-btn>
              <q-btn
                flat
                round
                dense
                size="sm"
                color="grey-7"
                :icon="symRoundedDownload"
                :href="`${previewDocument.original_url}?download=1`"
                target="_blank">
                <q-tooltip>{{ t('workbench.downloadFile') }}</q-tooltip>
              </q-btn>
            </div>
            <template v-if="isImage">
              <q-scroll-area class="col">
                <q-img
                  :src="previewDocument!.original_url"
                  fit="contain"
                  class="q-ma-sm"
                  style="max-height: 100%" />
              </q-scroll-area>
            </template>
            <template v-else-if="isPdf">
              <iframe
                :src="previewDocument!.original_url"
                class="col full-width"
                style="border: 0" />
            </template>
            <template v-else>
              <div class="col column items-center justify-center q-gutter-sm text-grey-6">
                <q-icon
                  :name="fileIconFor(previewDocument?.mime_type ?? item.mime_type)"
                  size="52px" />
                <div class="text-caption">{{ t('workbench.noPreview') }}</div>
                <q-btn
                  v-if="previewDocument"
                  outline
                  color="primary"
                  :icon="symRoundedDownload"
                  :label="t('workbench.downloadFile')"
                  :href="`${previewDocument.original_url}?download=1`"
                  target="_blank" />
              </div>
            </template>
          </div>

          <q-separator
            v-if="!$q.screen.lt.md"
            vertical />

          <!-- Details pane -->
          <q-scroll-area
            v-show="!$q.screen.lt.md || mobileTab === 'details'"
            class="col">
            <div class="q-pa-md column q-gutter-md">
              <!-- Meta -->
              <div class="workbench-meta">
              <div class="workbench-meta__item">
                <span class="workbench-meta__label">{{ t('workbench.meta.received') }}</span>
                <span>{{ formatDate(item.created_at, true) }}</span>
              </div>
              <div
                v-if="item.completed_at"
                class="workbench-meta__item">
                <span class="workbench-meta__label">{{ t('workbench.meta.completed') }}</span>
                <span>{{ formatDate(item.completed_at, true) }}</span>
              </div>
              <div
                v-if="item.duration_seconds !== null && !isActive"
                class="workbench-meta__item">
                <span class="workbench-meta__label">{{ t('workbench.meta.duration') }}</span>
                <span>{{ formatSeconds(item.duration_seconds) }}</span>
              </div>
              <div class="workbench-meta__item">
                <span class="workbench-meta__label">{{ t('workbench.meta.attempts') }}</span>
                <span>{{ item.retry_count }} / {{ item.max_retries }}</span>
              </div>
              <div
                v-if="item.confirmed_model_type"
                class="workbench-meta__item">
                <span class="workbench-meta__label">{{ t('workbench.meta.model') }}</span>
                <span>{{ modelLabel(item.confirmed_model_type) }}</span>
              </div>
            </div>

            <!-- Bill finalization: the one remaining user task on completed items -->
            <InfoBox
              v-if="billRecord"
              :label="t('workbench.sections.finalize')"
              theme="accent">
              <BillFinalizationPanel
                :bill-id="billRecord.id"
                @finalized="onBillFinalized" />
            </InfoBox>

            <!-- Pipeline -->
            <InfoBox
              :label="t('workbench.sections.pipeline')"
              theme="default">
              <PipelineTimeline
                :stages="item.stages"
                class="q-pt-sm" />
            </InfoBox>

            <!-- Generated records -->
            <InfoBox
              :label="t('workbench.sections.records')"
              theme="positive">
              <GeneratedRecords
                :records="item.generated_records"
                :show-hint="isActive"
                class="q-py-sm" />
            </InfoBox>

            <!-- Extracted data -->
            <InfoBox
              :label="t('workbench.sections.data')"
              theme="info">
              <div class="q-py-sm">
                <template v-if="extractedData">
                  <template v-if="extractionComponent && !extractionComponentFailed">
                    <component
                      :is="extractionComponent"
                      :data="extractedData" />
                  </template>
                  <ExtractedDataTree
                    v-else
                    :data="extractedData" />
                  <q-btn
                    flat
                    dense
                    size="sm"
                    color="grey-6"
                    class="q-mt-sm"
                    :icon="showRawJson ? symRoundedVisibilityOff : symRoundedDataObject"
                    :label="showRawJson ? t('workbench.data.hideRaw') : t('workbench.data.showRaw')"
                    @click="showRawJson = !showRawJson" />
                  <pre
                    v-if="showRawJson"
                    class="workbench-json">{{ JSON.stringify(extractedData, null, 2) }}</pre>
                </template>
                <div
                  v-else
                  class="text-caption text-grey-6">
                  {{ t('workbench.data.empty') }}
                </div>
              </div>
            </InfoBox>

            <!-- Errors -->
            <InfoBox
              v-if="errorEntries.length"
              :label="t('workbench.sections.errors')"
              theme="negative">
              <div class="column q-gutter-xs q-py-sm">
                <div
                  v-for="(entry, index) in errorEntries"
                  :key="index"
                  class="text-body2">
                  {{ entry }}
                </div>
              </div>
            </InfoBox>
            </div>
          </q-scroll-area>
        </div>
      </q-card-section>

      <q-separator />

      <!-- Footer actions -->
      <q-card-actions
        align="right"
        class="workbench-actions">
        <q-btn
          v-if="item && needsModelConfirmation"
          color="accent"
          :icon="symRoundedFactCheck"
          :label="t('modelSelection.title')"
          @click="onConfirmModel" />
        <q-btn
          v-if="item?.status === 'failed'"
          color="warning"
          :icon="symRoundedRefresh"
          :label="t('queue.retryProcessing')"
          :loading="acting"
          @click="onRetry" />
        <q-btn
          v-if="item && isActive"
          flat
          color="negative"
          :icon="symRoundedStopCircle"
          :label="t('queue.cancelProcessing')"
          :loading="acting"
          @click="onCancel" />
        <q-btn
          v-if="item && !isActive"
          flat
          color="grey-6"
          :icon="symRoundedDelete"
          :label="t('actions.delete')"
          :loading="acting"
          @click="onDelete" />
        <q-btn
          flat
          :label="t('actions.close')"
          @click="onDialogOK" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import {
  symRoundedCheckCircle,
  symRoundedClose,
  symRoundedContentCopy,
  symRoundedDataObject,
  symRoundedDelete,
  symRoundedDescription,
  symRoundedDownload,
  symRoundedFactCheck,
  symRoundedInfo,
  symRoundedOpenInNew,
  symRoundedRefresh,
  symRoundedStopCircle,
  symRoundedVisibilityOff,
} from '@quasar/extras/material-symbols-rounded'
import { copyToClipboard, useDialogPluginComponent, useQuasar } from 'quasar'
import useDate from 'src/CrudManager/Core/composables/useDate'
import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import InfoBox from 'src/CrudManager/Core/composites/Ui/InfoBox/InfoBox.vue'
import StatusPill from 'src/CrudManager/Core/composites/Ui/StatusPill/StatusPill.vue'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import { showErrorNotification, showSuccessNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import { computed, onErrorCaptured, onMounted, ref, watchEffect, type Component } from 'vue'
import { useAiDocumentApi } from '../composables/useAiDocumentApi'
import { openModelSelectorDialog } from '../composables/useModelSelectorDialog'
import type { AiQueueItem } from '../types'
import { ACTIVE_STATUSES, AiQueueStatus, getExtractedData } from '../types'
import { fileIconFor, STATUS_COLOR } from '../utils/statusMaps'
import BillFinalizationPanel from './BillFinalizationPanel.vue'
import ExtractedDataTree from './ExtractedDataTree.vue'
import GeneratedRecords from './GeneratedRecords.vue'
import PipelineTimeline from './PipelineTimeline.vue'

/**
 * Document workbench: uploaded file on the left, everything the pipeline
 * did with it on the right — stage timeline, created records (CrudPreview),
 * extracted data and errors — plus the item's contextual actions.
 */
const props = defineProps<{
  /** Item as known by the list (instant paint) — refreshed with stages on open */
  initialItem: AiQueueItem
  /** Called after any successful mutation so the opener can refresh its list */
  onMutated?: () => void
}>()

defineEmits([...useDialogPluginComponent.emits])

const { dialogRef, onDialogHide, onDialogOK } = useDialogPluginComponent()
const { t } = useCompositeI18n('core.AiDocumentProcessor')
const { formatDate } = useDate()
const $q = useQuasar()
const api = useAiDocumentApi()
const crudManagerStore = useCrudManagerStore()

const item = ref<AiQueueItem | null>(props.initialItem)
const loading = ref(false)
const acting = ref(false)
const copied = ref(false)
const showRawJson = ref(false)
const mobileTab = ref<'details' | 'document'>('details')

const isActive = computed(() => (item.value ? ACTIVE_STATUSES.includes(item.value.status) : false))

const needsModelConfirmation = computed(
  () => item.value?.status === 'awaiting_user_action' && item.value?.queue_step === 'awaiting_model_confirmation',
)

// Generated bill to finalize in place — only once the pipeline is done
const billRecord = computed(() => {
  if (item.value?.status !== AiQueueStatus.Completed) return null
  return item.value.generated_records?.find((record) => record.model === 'bill') ?? null
})

const previewDocument = computed(() => item.value?.documents?.[0] ?? null)
const isImage = computed(() => !!previewDocument.value?.mime_type?.startsWith('image/'))
const isPdf = computed(() => previewDocument.value?.mime_type === 'application/pdf')

const extractedData = computed(() => (item.value ? getExtractedData(item.value) : null))

const errorEntries = computed<string[]>(() => {
  const errors = item.value?.processing_errors
  if (!errors) return []
  return Object.entries(errors)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => (typeof value === 'string' ? value : `${key}: ${JSON.stringify(value)}`))
})

// Model-specific extraction renderer (e.g. transaction), tree as fallback
const extractionComponent = ref<Component | null>(null)
const extractionComponentFailed = ref(false)

watchEffect(async () => {
  extractionComponent.value = null
  if (!item.value?.confirmed_model_type) return
  try {
    extractionComponent.value = (await crudManagerStore.getModelComponent(item.value.confirmed_model_type, 'ai.data_extraction')) ?? null
  } catch {
    extractionComponent.value = null
  }
})

onErrorCaptured(() => {
  // A model-specific renderer choked on partial data — fall back to the tree
  if (extractionComponent.value && !extractionComponentFailed.value) {
    extractionComponentFailed.value = true
    return false
  }
  return true
})

const refetch = async (): Promise<void> => {
  if (!item.value) return
  loading.value = true
  try {
    const fresh = await api.getQueueStatus(item.value.id)
    if (fresh) {
      item.value = fresh
    }
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void refetch()
})

const modelLabel = (modelType: string): string => {
  const key = `records.models.${modelType}`
  const label = t(key)
  return label === `core.AiDocumentProcessor.${key}` ? modelType : label
}

const formatSeconds = (seconds: number): string => {
  if (seconds < 60) return `${Math.round(seconds)} s`
  return `${Math.floor(seconds / 60)} min ${Math.round(seconds % 60)} s`
}

const copyId = (): void => {
  if (!item.value) return
  void copyToClipboard(item.value.id).then(() => {
    copied.value = true
    setTimeout(() => (copied.value = false), 1500)
  })
}

// ── Actions ──────────────────────────────────────────────────────────────

const runAction = async (action: () => Promise<boolean>): Promise<boolean> => {
  acting.value = true
  try {
    const success = await action()
    if (success) {
      props.onMutated?.()
      await refetch()
    }
    return success
  } finally {
    acting.value = false
  }
}

const onRetry = (): void => {
  void runAction(() => api.retryProcessing(item.value!.id))
}

const onCancel = (): void => {
  $q.dialog({
    title: t('confirms.cancelTitle'),
    message: t('confirms.cancelMessage', { name: item.value?.original_filename || item.value?.id }),
    cancel: { flat: true, label: t('actions.cancel') },
    ok: { color: 'negative', label: t('actions.confirm') },
  }).onOk(() => {
    void runAction(() => api.cancelProcessing(item.value!.id))
  })
}

const onDelete = (): void => {
  $q.dialog({
    title: t('confirms.deleteTitle'),
    message: t('confirms.deleteMessage', { name: item.value?.original_filename || item.value?.id }),
    cancel: { flat: true, label: t('actions.cancel') },
    ok: { color: 'negative', label: t('actions.delete') },
  }).onOk(() => {
    void runAction(() => api.deleteQueue(item.value!.id)).then((success) => {
      if (success) onDialogOK()
    })
  })
}

const onConfirmModel = (): void => {
  if (!item.value) return
  void openModelSelectorDialog(item.value).then(async () => {
    props.onMutated?.()
    await refetch()
  })
}

// Bill finalized from the panel: the queue item has served its purpose —
// remove it automatically and close the workbench.
const onBillFinalized = async (): Promise<void> => {
  if (!item.value) return
  const removed = await api.deleteQueue(item.value.id)
  props.onMutated?.()
  if (removed) {
    showSuccessNotification(t('workbench.finalize.successRemoved'))
    onDialogOK()
  } else {
    showErrorNotification(t('workbench.finalize.removeFailed'))
    await refetch()
  }
}
</script>

<style lang="scss" scoped>
.workbench-body {
  min-height: 0;
}

.workbench-panes {
  min-height: 0;
  flex: 1;
}

.workbench-preview {
  width: 42%;
  min-width: 320px;
  background: color-mix(in srgb, #000 4%, transparent);

  .body--dark & {
    background: color-mix(in srgb, #000 25%, transparent);
  }

  &--mobile {
    width: 100%;
    min-width: 0;
  }
}

.workbench-actions {
  flex-wrap: wrap;
  row-gap: 4px;
}

.workbench-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px 16px;

  &__item {
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    opacity: 0.6;
  }
}

.workbench-json {
  margin: 8px 0 0;
  padding: 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  overflow: auto;
  max-height: 320px;
  background: color-mix(in srgb, #000 6%, transparent);

  .body--dark & {
    background: color-mix(in srgb, #000 30%, transparent);
  }
}
</style>
